import { Injectable, Inject, Renderer2, RendererFactory2 } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { BehaviorSubject } from 'rxjs';
import { TokenStorageService } from '../token-storage/token-storage.service';
import { LocalStorageUtil } from '../../../../core/utils/local-storage-util';

@Injectable({
  providedIn: 'root'
})
export class IntercomService {

  private renderer: Renderer2;
  loadedIntercomWidget = false;
  isDisplayIntercomWidget$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(
    private rendererFactory: RendererFactory2,
    @Inject(DOCUMENT) private _document: any,
    private tokenStorageService: TokenStorageService
  ) {
    this.renderer = this.rendererFactory.createRenderer(null, null);
  }

  openChat() {
    this.isDisplayIntercomWidget$.next(true);
    this.tokenStorageService.setChatWidgetStatus();

    if (this.loadedIntercomWidget) {
      (<any>window).Intercom('show');
    } else {
      this.loadIntercomScript();
    }
  }

  private loadIntercomScript() {
    const userData = LocalStorageUtil.getUser();
    const APP_ID = 'lj3iffbg';

    // Setting up Intercom with user data
    (window as any).intercomSettings = {
      app_id: APP_ID,
      name: `${userData.user_first_name} ${userData.user_last_name}`, 
      email: userData.user_email_address,
      phone: userData.user_country_code + userData.user_phone_number,
      user_id: userData.user_id,
      company : {
        id: userData.businessViewModel.business_id,
        name  : userData.businessViewModel.business_name
      },
      created_at : userData.user_created_at,
      avatar: {
        type: "avatar", 
        image_url :userData.user_profile_url
      }
    };

    const script = this.renderer.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.src = `https://widget.intercom.io/widget/${APP_ID}`;
    script.onload = () => {
      this.loadedIntercomWidget = true;
      if (typeof (<any>window).Intercom === 'function') {
        (<any>window).Intercom('boot', (window as any).intercomSettings);
      }
      this.checkAndOpenWidget();
    };

    this.renderer.appendChild(this._document.body, script);
  }

  private checkAndOpenWidget() {
    if (typeof (<any>window).Intercom !== 'undefined' && (<any>window).Intercom === 'function') {
      (<any>window).Intercom('show');
    } else {
      console.warn('Intercom widget is not fully initialized yet after several retries.');
    }
  }

  closeChat(clearStorage: boolean = false) {
    clearStorage && this.tokenStorageService.removeChatWidgetStatus();
    this.isDisplayIntercomWidget$.next(false);

    if (typeof (<any>window).Intercom !== 'undefined' && typeof (<any>window).Intercom === 'function') {
      (<any>window).Intercom('shutdown'); // This will clear user data and hide the icon
      this.loadedIntercomWidget = false; // Reset the loaded state
    }
  }

  removeAllIntercomScripts() {
    this.closeChat();
    
    let removeScripts: HTMLElement[] = [];
    const scripts = this._document.getElementsByTagName('script');
    for (let i = 0; i < scripts.length; i++) {
      if (scripts[i].src && scripts[i].src.includes('intercom.io/widget.js') > -1) {
        removeScripts.push(scripts[i]);
      }
    }

    const mainIntercomScript = this._document.getElementById('intercom_custom_script');
    if (mainIntercomScript) {
      removeScripts.push(mainIntercomScript);
    }

    for (let script of removeScripts) {
      script.parentNode && script.parentNode.removeChild(script);
    }
  }

  onUserLogout() {
    this.clearIntercomUser();
  }

  // Method to clear Intercom user session
  clearIntercomUser() {
    if (typeof (<any>window).Intercom !== 'undefined' && typeof (<any>window).Intercom === 'function') {
      (<any>window).Intercom('shutdown'); // This will effectively clear user data from the Intercom widget
      this.loadedIntercomWidget = false;
      this.removeAllIntercomScripts();
    }
  }
}
